/**
 * Every sound in the app (the peon's lines, the tour readings, the chiptune acknowledgements, the
 * ticks and screen cues, the push alert) plays through one shared Web Audio context, never through
 * an <audio> element: WebKit registers any playing media element as a Now Playing session, so iOS
 * put a media player on the lock screen and in Control Center whenever the peon spoke, and the lines
 * ignored the silent switch. Web Audio registers nothing. A Clip below stands in for the element the
 * rest of the app used to be handed; only a browser without AudioContext still gets elements.
 */
import { isFeatureEnabled } from "./flags-core.ts";
import type { FlashState } from "../shared/types";
import { parseHash, type Route } from "./router.ts";
import { getPeonName } from "./store.ts";
import { TUNE_BASE, tuneFile, type TrinketKey } from "../shared/trinkets.ts";
import { HELLO_WAIT_MS, PERSONAL_BASE, launchSaysHello, personalLineText, personalLineUrl, type PersonalLineKey } from "../shared/voicelines.ts";
import { notePersonalClip, personalEnvelopes, personalHeaders, personalText, within, type ChosenLine } from "../shared/voice-player.ts";

const MANIFEST_URL = "/audio/ui/manifest.json";
const BASE = "/audio/ui/";
const UI_ENCODED_URL = `${BASE}encoded.json`;

// Warcraft peon lines (today's default) and their original replacements (originalAssets flag).
// Both directories share the same shape: a manifest.json of every line, an encoded.json marking
// which have a matching .m4a, and one reserved greeting plus one reserved completion line that
// the random rotation (playRandomPeonLine) always excludes.
const PEON_BASE = "/audio/peons/";
const ADD_TASK_GREETING = "something-need-doing.wav";
const WORK_COMPLETE = "work-complete.wav";
const PEON_BASE_ORIGINAL = "/audio/peons-original/";
const ADD_TASK_GREETING_ORIGINAL = "add-task-greeting.wav";
const WORK_COMPLETE_ORIGINAL = "task-done.wav";

/**
 * What the peon says, by moment. The original set (originalAssets on) maps each role to its own
 * lines in public/audio/peons-original/roles.json; `idle` is the random pool (launch greeting and
 * incidental remarks), `greeting` opens Add Task and `done` finishes a job. `open` is being handed
 * a job (the task screen opens) and `abandon` is backing out of it. `timer` and `notify` are the
 * timed note's ring and the notification lines (shared/push-sounds.ts); `badge` and `rank` are
 * a badge landing and a rank going up (docs/achievements.md), said through their pushes. The rest are action lines,
 * played once on the action that names them.
 */
export const PEON_ROLES = [
  "greeting", "done", "open", "abandon", "added", "reopen", "delete", "urgent", "bigjob",
  "redeem", "got", "luxury", "rush", "note", "timer", "notify", "badge", "rank", "idle",
] as const;
export type PeonRole = (typeof PEON_ROLES)[number];

/**
 * Greetings and completions: they replace whatever is playing, and an action line never interrupts
 * them (it is dropped instead). `idle` here is the launch greeting; incidental remarks draw from the
 * same pool but go through maybePlayPeonRemark, which yields to everything.
 */
const PRIORITY_ROLES: ReadonlySet<PeonRole> = new Set<PeonRole>(["greeting", "done", "idle"]);

/**
 * When a set has no lines for a role (the Warcraft set, which has no roles.json, or the original set
 * when roles.json fails to load), these roles borrow a random line from the pool. Every other action
 * role stays silent: a random "Zug zug" after pinning a note would be noise, not character.
 */
const POOL_FALLBACK_ROLES: ReadonlySet<PeonRole> = new Set<PeonRole>(["idle", "open", "abandon", "added", "delete"]);

interface PeonAudioConfig {
  base: string;
  manifestUrl: string;
  encodedUrl: string;
  /** Role map for this set, or null when the set has none (the Warcraft set). */
  rolesUrl: string | null;
  greeting: string;
  complete: string;
}

/**
 * Which peon voice directory to play from, read fresh on every call (not cached at module load):
 * the originalAssets flag can change live while the app is open (the workshop's "Everyone" switch,
 * or this device's own toggle), so a line already mid-playback keeps going but the next one always
 * reflects the flag as it stands right now.
 */
function peonAudioConfig(): PeonAudioConfig {
  if (isFeatureEnabled("originalAssets")) {
    return {
      base: PEON_BASE_ORIGINAL,
      manifestUrl: `${PEON_BASE_ORIGINAL}manifest.json`,
      encodedUrl: `${PEON_BASE_ORIGINAL}encoded.json`,
      rolesUrl: `${PEON_BASE_ORIGINAL}roles.json`,
      greeting: ADD_TASK_GREETING_ORIGINAL,
      complete: WORK_COMPLETE_ORIGINAL,
    };
  }
  return {
    base: PEON_BASE,
    manifestUrl: `${PEON_BASE}manifest.json`,
    encodedUrl: `${PEON_BASE}encoded.json`,
    rolesUrl: null,
    greeting: ADD_TASK_GREETING,
    complete: WORK_COMPLETE,
  };
}

const manifests = new Map<string, string[]>();

// Whether this browser can play AAC-LC in an .m4a container. Cached once:
// it can't change during a page's lifetime. Each directory's `encoded.json`
// (git-ignored under peons/, committed under ui/; written by
// `npm run audio:encode`) lists which .wav names have a matching .m4a; a
// name missing from it, or absent because the marker itself 404s on a
// checkout that hasn't run the encoder, falls back to the .wav untouched.
let canPlayAac: boolean | null = null;

function supportsEncodedAudio(): boolean {
  if (canPlayAac === null) {
    try {
      // An element that is only asked, never played, registers no Now Playing session. The
      // Audio constructor is the fallback for a document without createElement (the tests').
      const probe = typeof document !== "undefined" && typeof document.createElement === "function"
        ? document.createElement("audio")
        : new Audio();
      canPlayAac = !!probe.canPlayType('audio/mp4; codecs="mp4a.40.2"');
    } catch {
      canPlayAac = false;
    }
  }
  return canPlayAac;
}

// ---------- Playback: one shared AudioContext and the Clip ----------

/**
 * What a playing sound looks like to the code that follows it (the peon's mouth, the speech bubble,
 * the tour's ad-lib): the part of HTMLAudioElement they use, which both an element and a Clip have.
 */
export interface PlayingClip {
  readonly paused: boolean;
  readonly ended: boolean;
  readonly currentTime: number;
  readonly duration: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void;
}

let sharedContext: AudioContext | null | undefined;

/** The one AudioContext for every sound, or null where the browser has no Web Audio. */
function audioCtx(): AudioContext | null {
  if (sharedContext === undefined) {
    try {
      sharedContext = typeof AudioContext === "undefined" ? null : new AudioContext();
    } catch {
      sharedContext = null;
    }
  }
  return sharedContext;
}

/**
 * Asks a stopped context to run. Called on every press and at the start of anything played from a
 * gesture, because the browser only lets a gesture wake it, and iOS parks it in a WebKit-only
 * "interrupted" state after a call, Siri or a trip to the background and inside the app shell it
 * stays there until asked. Anything but "running" gets a resume.
 */
function wakeAudio(): void {
  const ctx = audioCtx();
  if (!ctx) return;
  if (ctx.state === "running") {
    wakeAskedAt = 0;
    return;
  }
  const now = Date.now();
  // "interrupted" is WebKit's own state: resume() does nothing until the interruption ends, and
  // after the camera hand-off it may never end (the owner's Job's Done went silent that way). A
  // gesture may start a fresh context now, so the very press gets it, not the one after. Any other
  // stopped state gets a resume first and a fresh context only if that did not take.
  const interrupted = (ctx.state as string) === "interrupted" && userActivationActive();
  const stale = wakeAskedAt !== 0 && now - wakeAskedAt > RESUME_WAIT_MS;
  if ((interrupted || stale) && contextReplacements < MAX_CONTEXT_REPLACEMENTS && now >= replaceHoldUntil) {
    replaceAudioContext(ctx);
    return;
  }
  if (!wakeAskedAt) wakeAskedAt = now;
  ctx.resume().catch(() => {});
}

/** Whether the page is inside a user gesture's activation window (true where the browser cannot say). */
function userActivationActive(): boolean {
  if (typeof navigator === "undefined") return true;
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return activation ? activation.isActive : true;
}

/** When a press first found the context stopped and asked it to resume; 0 once it runs again. */
let wakeAskedAt = 0;
let contextReplacements = 0;
/** Enough to recover from every interruption in a day's use; not a loop where sound is simply blocked. */
const MAX_CONTEXT_REPLACEMENTS = 20;
/** No fresh context before this time: the last one would not run either, so the block is outside the page (a call). */
let replaceHoldUntil = 0;
export const REPLACE_HOLD_MS = 30_000;

type AudioEventListener = (kind: string, message: string, detail: Record<string, unknown>) => void;
const audioEventListeners = new Set<AudioEventListener>();

/**
 * What the sound system wants the Device log to know (src/telemetry.ts reports each one as an
 * event): "audio.context.replaced" with the stale state, "audio.line.blocked" when a greeting or
 * completion could not start, "audio.line.played" and "audio.line.dropped" for the completion line.
 */
export function onAudioEvent(listener: AudioEventListener): () => void {
  audioEventListeners.add(listener);
  return () => audioEventListeners.delete(listener);
}

/** Called with the stale context's state each time the app starts a fresh one. */
export function onAudioContextReplaced(listener: (staleState: string) => void): () => void {
  return onAudioEvent((kind, message) => {
    if (kind === "audio.context.replaced") listener(message);
  });
}

function emitAudioEvent(kind: string, message: string, detail: Record<string, unknown> = {}): void {
  for (const listener of audioEventListeners) {
    try {
      listener(kind, message, detail);
    } catch {
      /* a listener's failure must not silence the app */
    }
  }
}

/** The shared context's state for the Device log, "none" where there is no Web Audio. */
function contextState(): string {
  return sharedContext ? sharedContext.state : "none";
}

/**
 * Starts over with a new context. The old one is "interrupted" under a gesture, or a press asked
 * it to resume, RESUME_WAIT_MS has passed, and it still is not running: WebKit does not honour
 * resume() while it holds a context "interrupted" (a call, Siri, another app's sound, the camera,
 * the app shell going to the background), and the interruption does not always end on its own.
 * Before v169 a voice line's <audio> element reopened the audio session as a side effect and the
 * ticks came back with it; now nothing plays outside the context, so the owner's phone went silent
 * for good. A context made inside a press starts running (it is asked to, in case it comes up
 * suspended). The line in flight is stopped properly (its bubble and mouth follow "pause"), the
 * decoded cues belong to the old context and are fetched again, and the old one is closed. If the
 * fresh one will not run either the block is outside the page, and no more are made for a while.
 */
function replaceAudioContext(stale: AudioContext): void {
  const staleState = stale.state;
  contextReplacements++;
  wakeAskedAt = 0;
  launchAudio?.pause();
  sharedContext = undefined;
  cueBuffers.clear();
  stale.close().catch(() => {});
  preloadUiCues();
  const fresh = audioCtx();
  if (fresh && fresh.state !== "running") {
    fresh.resume().catch(() => {});
    setTimeout(() => {
      if (sharedContext === fresh && (fresh.state as string) !== "running") replaceHoldUntil = Date.now() + REPLACE_HOLD_MS;
    }, RESUME_WAIT_MS);
  }
  emitAudioEvent("audio.context.replaced", staleState, { state: staleState, fresh: fresh?.state ?? "none" });
}

/** How long play() waits for a stopped context to start before treating sound as blocked. */
const RESUME_WAIT_MS = 500;

/**
 * Whether the context is running, resuming it first if it is not. The resume is asked for before the
 * first await so a gesture handler that calls play() still counts as the gesture. A blocked resume
 * can stay pending forever (Chrome) or settle with the context still suspended (Safari); both read
 * as not running after RESUME_WAIT_MS.
 */
async function contextRunning(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === "running") return true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      ctx.resume(),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, RESUME_WAIT_MS); }),
    ]);
  } catch {
    /* a refused resume is the same as a blocked one */
  } finally {
    clearTimeout(timer);
  }
  return (ctx.state as string) === "running";
}

/** How often a playing Clip reports its progress (the bubble pages its text by it). */
const CLIP_TIMEUPDATE_MS = 100;

/**
 * A decoded sound played once through the shared context, shaped like the element it replaced:
 * play() rejects when sound cannot start (as an element's does on blocked autoplay, which the
 * greeting's gesture retry relies on), pause() stops it and fires "pause", a natural finish fires
 * "ended", never both, and "timeupdate" ticks while it plays. A Clip lets go of its samples as soon
 * as it is paused or ends, because nothing here ever resumes a stopped line and a decoded voice line
 * is about a megabyte: a later play() rejects.
 */
export class Clip extends EventTarget implements PlayingClip {
  paused = true;
  ended = false;
  readonly duration: number;
  private buffer: AudioBuffer | null;
  private source: AudioBufferSourceNode | null = null;
  /** Context time the clip started at; valid while a source plays. */
  private startedAt = 0;
  /** Where the clip stood when it last stopped. */
  private position = 0;
  /** Bumped by pause(), so a play() still waiting on the context does not start after it. */
  private pauses = 0;
  private ticker: ReturnType<typeof setInterval> | undefined;
  /** The play() still waiting on the context, so a second call shares its outcome. */
  private starting: Promise<void> | null = null;

  private readonly ctx: AudioContext;

  constructor(ctx: AudioContext, buffer: AudioBuffer) {
    super();
    this.ctx = ctx;
    this.buffer = buffer;
    this.duration = buffer.duration;
  }

  get currentTime(): number {
    if (!this.source) return this.position;
    return Math.min(this.duration, Math.max(0, this.ctx.currentTime - this.startedAt));
  }

  play(): Promise<void> {
    const buffer = this.buffer;
    if (!buffer) return Promise.reject(new DOMException("This clip has been released.", "InvalidStateError"));
    if (!this.paused) return this.starting ?? Promise.resolve();
    if (this.ctx.state === "running") {
      this.start(buffer);
      return Promise.resolve();
    }
    // Waiting on the context counts as playing, as a loading element does, so the line holds the
    // voice channel (priorityLineBusy, remarks) until it either starts or is refused.
    this.paused = false;
    const pauses = this.pauses;
    this.starting = contextRunning(this.ctx).then((running) => {
      this.starting = null;
      if (pauses !== this.pauses || !this.buffer) {
        throw new DOMException("The play() request was interrupted by a call to pause().", "AbortError");
      }
      if (!running) {
        this.paused = true;
        throw new DOMException("Sound is not allowed to start yet.", "NotAllowedError");
      }
      this.start(this.buffer);
    });
    return this.starting;
  }

  pause(): void {
    this.pauses++;
    const source = this.source;
    if (source) {
      this.position = this.currentTime;
      this.source = null;
      try {
        source.stop();
      } catch {
        /* already finished */
      }
    }
    const wasPlaying = !this.paused;
    this.finish();
    if (wasPlaying) this.dispatchEvent(new Event("pause"));
  }

  private start(buffer: AudioBuffer): void {
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    // onended fires for a stop() too; pause() has already let go of the source by then, so only a
    // source that is still current finished on its own.
    source.onended = () => {
      if (this.source !== source) return;
      this.source = null;
      this.position = this.duration;
      this.ended = true;
      this.finish();
      this.dispatchEvent(new Event("ended"));
    };
    this.source = source;
    this.startedAt = this.ctx.currentTime;
    this.paused = false;
    source.start();
    this.ticker = setInterval(() => this.dispatchEvent(new Event("timeupdate")), CLIP_TIMEUPDATE_MS);
  }

  private finish(): void {
    this.paused = true;
    this.buffer = null;
    clearInterval(this.ticker);
    this.ticker = undefined;
  }
}

/**
 * The sound at `src`, ready to play: fetched and decoded into a Clip on the shared context, or an
 * element where there is no Web Audio. Null when it could not be loaded or decoded. Voice lines,
 * readings and acknowledgements are decoded afresh each time and never kept (see Clip); only the
 * three UI cues are cached, and they are a few KB.
 */
function loadClip(src: string): PlayingClip | null | Promise<PlayingClip | null> {
  const ctx = audioCtx();
  if (!ctx) {
    const audio = new Audio(src);
    audio.preload = "auto";
    return audio;
  }
  return decodeClip(ctx, src);
}

async function decodeClip(ctx: AudioContext, src: string): Promise<Clip | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    return new Clip(ctx, await ctx.decodeAudioData(await res.arrayBuffer()));
  } catch {
    return null;
  }
}

/** Plays one sound on its own, outside the peon's voice channel (the push alert in src/push.ts). */
export function playSound(src: string): void {
  void (async () => {
    const clip = await loadClip(src);
    await clip?.play();
  })().catch(() => {});
}

async function resolveSrc(base: string, encodedUrl: string, name: string): Promise<string> {
  if (supportsEncodedAudio()) {
    const encoded = await loadManifest(encodedUrl);
    if (encoded.includes(name)) return base + encodeURIComponent(name.replace(/\.wav$/, ".m4a"));
  }
  return base + encodeURIComponent(name);
}

async function loadManifest(url = MANIFEST_URL): Promise<string[]> {
  const cached = manifests.get(url);
  if (cached) return cached;
  let manifest: string[];
  try {
    const res = await fetch(url, { cache: "no-cache" });
    const list = (await res.json()) as unknown;
    manifest = Array.isArray(list) ? list.filter((x): x is string => typeof x === "string" && x.length > 0) : [];
  } catch {
    manifest = [];
  }
  manifests.set(url, manifest);
  return manifest;
}

type RoleMap = Partial<Record<PeonRole, string[]>>;

// roles.json, cached per URL like the manifests: one fetch per page load. A missing or malformed
// file caches null, and every role then falls back to the manifest behaviour (linesFor below).
const roleCaches = new Map<string, Promise<RoleMap | null>>();
const settledRoles = new Map<string, RoleMap | null>();

function loadRoles(url: string): Promise<RoleMap | null> {
  let cached = roleCaches.get(url);
  if (!cached) {
    cached = (async () => {
      let roles: RoleMap | null = null;
      try {
        const res = await fetch(url, { cache: "no-cache" });
        const data = res.ok ? ((await res.json()) as unknown) : null;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          roles = {};
          for (const [key, list] of Object.entries(data as Record<string, unknown>)) {
            if (!Array.isArray(list)) continue;
            const files = list.filter((x): x is string => typeof x === "string" && x.length > 0);
            if (files.length > 0) roles[key as PeonRole] = files;
          }
        }
      } catch {
        roles = null;
      }
      settledRoles.set(url, roles);
      return roles;
    })();
    roleCaches.set(url, cached);
  }
  return cached;
}

/**
 * The lines `role` can play in the current set: its roles.json entry when there is one, otherwise
 * today's manifest behaviour (the reserved greeting or completion clip, the random pool for idle and
 * the POOL_FALLBACK_ROLES, nothing for the rest).
 */
export async function peonLinesFor(role: PeonRole): Promise<string[]> {
  return linesFor(role, peonAudioConfig());
}

async function linesFor(role: PeonRole, cfg: PeonAudioConfig): Promise<string[]> {
  if (cfg.rolesUrl) {
    const list = (await loadRoles(cfg.rolesUrl))?.[role];
    if (list && list.length > 0) return list;
  }
  if (role === "greeting") return [cfg.greeting];
  if (role === "done") return [cfg.complete];
  if (!POOL_FALLBACK_ROLES.has(role)) return [];
  return (await loadManifest(cfg.manifestUrl)).filter((line) => line !== cfg.greeting && line !== cfg.complete);
}

/** False only when `role` is already known to be silent in this set, so a silent action stops nothing. */
function mayHaveLines(role: PeonRole, cfg: PeonAudioConfig): boolean {
  if (role === "greeting" || role === "done" || POOL_FALLBACK_ROLES.has(role)) return true;
  if (!cfg.rolesUrl) return false;
  if (!settledRoles.has(cfg.rolesUrl)) return true;
  return !!settledRoles.get(cfg.rolesUrl)?.[role]?.length;
}

/** Short original chiptune acknowledgements used when a form is submitted. */
const ACK_LINES = ["ack-1.wav", "ack-2.wav", "ack-3.wav", "ack-4.wav"];

/**
 * A random line that is not one of the last few played from the same list: half the list is held
 * back (at least one line), so a two-line role alternates, a six-line role never repeats within three
 * and the idle pool goes twenty lines before a line comes round again. Plain random with replacement
 * repeated "Again? Really?" three times running, which reads as broken rather than dopey. The recent
 * lines are kept per list in localStorage so the first line after a relaunch is not the last one
 * heard either.
 */
const RECENT_LINES_KEY = "op.recentLines";
let recentLines: Record<string, string[]> | null = null;

function loadRecentLines(): Record<string, string[]> {
  if (recentLines) return recentLines;
  recentLines = {};
  try {
    const raw = localStorage.getItem(RECENT_LINES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (Array.isArray(value) && value.every((item) => typeof item === "string")) recentLines[key] = value;
      }
    }
  } catch {
    /* storage unavailable or corrupt: recent lines start empty */
  }
  return recentLines;
}

function pick(list: string[]): string {
  if (list.length < 2) return list[0];
  const recent = loadRecentLines();
  const key = list.join("\n");
  const held = Math.min(list.length - 1, Math.max(1, Math.floor(list.length / 2)));
  const avoid = new Set((recent[key] ?? []).slice(-held));
  const candidates = list.filter((name) => !avoid.has(name));
  const name = candidates[Math.floor(Math.random() * candidates.length)];
  recent[key] = [...(recent[key] ?? []).filter((item) => item !== name), name].slice(-held);
  try {
    localStorage.setItem(RECENT_LINES_KEY, JSON.stringify(recent));
  } catch {
    /* best effort */
  }
  return name;
}

// ---------- Peon speech subscription (src/components/LivePeon.tsx) ----------
//
// Fires only when a PEON line (never a UI acknowledgement) actually starts
// audible playback, including the deferred gesture-retry path below: a line
// that never got past a blocked autoplay never notifies. `dir` and `name`
// are the voice directory (playPeonLine's `cfg.base`, e.g. "/audio/peons/")
// and the line's original .wav name, which is how LivePeon looks the clip up
// in that directory's envelopes.json regardless of whether the .wav or the
// encoded .m4a actually played.
type PeonSpeechListener = (audio: PlayingClip, dir: string, name: string) => void;
const speechListeners = new Set<PeonSpeechListener>();
let currentSpeech: { audio: PlayingClip; dir: string; name: string } | null = null;

/** Subscribe, joining a playing line when a header mounts after launch or navigation. */
export function onPeonSpeech(listener: PeonSpeechListener): () => void {
  speechListeners.add(listener);
  if (currentSpeech && !currentSpeech.audio.paused && !currentSpeech.audio.ended) {
    try { listener(currentSpeech.audio, currentSpeech.dir, currentSpeech.name); } catch { /* Keep playback independent. */ }
  }
  return () => {
    speechListeners.delete(listener);
  };
}

function notifyPeonSpeech(audio: PlayingClip, dir: string, name: string): void {
  if (audio !== launchAudio || audio.paused || audio.ended) return;
  currentSpeech = { audio, dir, name };
  lastPeonSpeechAt = Date.now();
  for (const listener of speechListeners) {
    try {
      listener(audio, dir, name);
    } catch {
      // A listener's own bug must never break audio playback.
    }
  }
}

// ---------- Mouth envelopes (LivePeon's speaking animation) ----------
//
// Cached like the manifests above: one fetch per directory per page load. A
// missing envelopes.json (not yet built by npm run audio:encode) or any
// fetch/parse failure caches `null`, so LivePeon falls back to a random flap
// without retrying every time a line plays.
const envelopeCaches = new Map<string, Record<string, number[]> | null>();

/** The decoded mouth envelopes for every line in `dir` (e.g. "/audio/peons/"), or null if none are available. */
export async function loadEnvelopes(dir: string): Promise<Record<string, number[]> | null> {
  if (dir === PERSONAL_BASE) return personalEnvelopes();
  if (envelopeCaches.has(dir)) return envelopeCaches.get(dir) ?? null;
  let envelopes: Record<string, number[]> | null = null;
  try {
    const res = await fetch(`${dir}envelopes.json`, { cache: "no-cache" });
    if (res.ok) {
      const data = (await res.json()) as unknown;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        envelopes = data as Record<string, number[]>;
      }
    }
  } catch {
    envelopes = null;
  }
  envelopeCaches.set(dir, envelopes);
  return envelopes;
}

let launchRequest = 0;
let launchAudio: PlayingClip | null = null;
let removePendingGesture = () => {};
let lastPeonSpeechAt = 0;
let lastPeonRequestAt = 0;
let nextRemarkAt = 0;
export const PEON_REMARK_COOLDOWN_MS = 45_000;

/**
 * The line on the peon's one voice channel: a greeting or completion ("priority"), an action line,
 * or an incidental remark. Only one ever plays; the rules for who gives way are on playPeonLine.
 */
type LineClass = "priority" | "action" | "incidental" | "tour";
let lineClass: LineClass | null = null;
/** True from a line's request until its clip exists (roles, manifest and encoded lookups, fetch and decode). */
let lineLoading = false;
/** True while a blocked greeting or completion waits for the next tap or key to retry. */
let retryPending = false;
/** Action lines stay quiet this long after a camera or library hand-off starts (silencePeon). */
export const HANDOFF_QUIET_MS = 1500;
let quietUntil = 0;
/** When the last camera or library hand-off started, until the app's next return to the front (startPeonFocusAudio). */
let handoffAt = 0;

/** A greeting or completion is loading, playing, or waiting for a gesture to retry. */
function priorityLineBusy(): boolean {
  if (lineClass !== "priority") return false;
  if (lineLoading || retryPending) return true;
  return !!launchAudio && !launchAudio.paused && !launchAudio.ended;
}

/** Incidental lines never replace a greeting/completion or queue an autoplay retry. */
export async function maybePlayPeonRemark(): Promise<void> {
  const now = Date.now();
  if (!isFeatureEnabled("livePeon") || document.visibilityState === "hidden" ||
      now < nextRemarkAt || now - Math.max(lastPeonSpeechAt, lastPeonRequestAt) < PEON_REMARK_COOLDOWN_MS ||
      (launchAudio && !launchAudio.paused && !launchAudio.ended) || Math.random() >= .15) return;
  nextRemarkAt = now + PEON_REMARK_COOLDOWN_MS;
  await playLine("idle", "incidental");
}

/**
 * Idle remarks: when nobody has done anything for a while the peon mumbles a pool line, then again at
 * longer, random intervals for as long as nothing happens. Doing something is tapping a control,
 * typing or changing screen; scrolling, moving the pointer and reading are not. Every line the peon
 * speaks for another reason also counts, so a remark never lands right after a greeting. The remark
 * yields like any incidental line: never over speech, never while hidden, never after a hand-off.
 */
export const IDLE_REMARK_AFTER_MS: readonly [number, number] = [18_000, 35_000];
export const IDLE_REMARK_AGAIN_MS: readonly [number, number] = [45_000, 90_000];

async function playIdleRemark(): Promise<void> {
  const now = Date.now();
  if (document.visibilityState === "hidden" || now < quietUntil ||
      (launchAudio && !launchAudio.paused && !launchAudio.ended) || retryPending || lineLoading) return;
  nextRemarkAt = now + PEON_REMARK_COOLDOWN_MS;
  await playLine("idle", "incidental");
}

export function startPeonIdleAudio(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const schedule = ([min, max]: readonly [number, number]) => {
    clear();
    timer = setTimeout(fire, min + Math.random() * (max - min));
  };
  const fire = () => {
    timer = null;
    if (document.visibilityState === "hidden") return;
    // Something was said meanwhile (a greeting, an action line): that was activity, wait again.
    const sinceSpeech = Date.now() - Math.max(lastPeonSpeechAt, lastPeonRequestAt);
    if (sinceSpeech < IDLE_REMARK_AFTER_MS[0]) {
      schedule([IDLE_REMARK_AFTER_MS[0] - sinceSpeech, IDLE_REMARK_AFTER_MS[1] - sinceSpeech]);
      return;
    }
    void playIdleRemark().then(() => schedule(IDLE_REMARK_AGAIN_MS));
  };
  const acted = (event: Event) => {
    if (!event.isTrusted) return;
    if (event.type === "click") {
      const target = event.target as { closest?: (selector: string) => unknown } | null;
      if (!target?.closest?.("button, a, [role='button'], input, select, textarea, label, summary")) return;
    }
    schedule(IDLE_REMARK_AFTER_MS);
  };
  const visibility = () => {
    if (document.visibilityState === "hidden") clear();
    else schedule(IDLE_REMARK_AFTER_MS);
  };
  document.addEventListener("click", acted);
  document.addEventListener("keydown", acted);
  document.addEventListener("input", acted);
  window.addEventListener("hashchange", acted);
  document.addEventListener("visibilitychange", visibility);
  visibility();
  return () => {
    clear();
    document.removeEventListener("click", acted);
    document.removeEventListener("keydown", acted);
    document.removeEventListener("input", acted);
    window.removeEventListener("hashchange", acted);
    document.removeEventListener("visibilitychange", visibility);
  };
}

/** Only ordinary intentional control clicks, not typing, submits or workshop toggles. */
export function startPeonClickAudio(): () => void {
  const onClick = (event: MouseEvent) => {
    if (!event.isTrusted || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const control = target.closest("button, a, [role='button']");
    if (!control || control.matches(":disabled, [aria-disabled='true']") ||
        control.closest("form, .feature-row, [data-peon-quiet]") ||
        control.getAttribute("href")?.startsWith("#/new")) return;
    // Let the control's own handler run first. A priority line started there wins.
    void maybePlayPeonRemark();
  };
  document.addEventListener("click", onClick);
  return () => document.removeEventListener("click", onClick);
}

/**
 * Stops any peon line (and so its speaking animation) and holds off incidental remarks for a
 * cooldown. Called just before handing off to the camera or photo library (src/photo-pick.ts), so
 * nothing is playing or animating while another app has the foreground.
 */
export function silencePeon(): void {
  stopLaunchAudio();
  handoffAt = Date.now();
  nextRemarkAt = Date.now() + PEON_REMARK_COOLDOWN_MS;
  quietUntil = Date.now() + HANDOFF_QUIET_MS;
}

/** How long the hand-off mark outlives the picker's answer, so a return seen just after it is still the hand-off ending. */
const HANDOFF_SETTLE_MS = 3000;

/**
 * The hand-off is over: the picker answered with a file or a cancel (src/photo-pick.ts). A desktop
 * file picker never hides the page, so without this the mark set by silencePeon would outlast the
 * pick and swallow the next real return's greeting. The mark is kept a few seconds longer, because
 * on a phone the page's return to view and the file's arrival come in either order.
 */
export function endPeonHandoff(): void {
  if (handoffAt === 0) return;
  handoffAt = Math.min(handoffAt, Date.now() - HANDOFF_RETURN_MS + HANDOFF_SETTLE_MS);
}

function stopLaunchAudio(): void {
  launchRequest++;
  currentSpeech = null;
  removePendingGesture();
  launchAudio?.pause();
  launchAudio = null;
  lineClass = null;
  lineLoading = false;
  retryPending = false;
}

/** The startled lines an open app plays when a push arrives (src/push.ts), from the Warcraft set. */
export const PUSH_ALERT_LINES = ["look-out.wav", "whaaat.wav", "huh.wav", "itog.wav", "zog-tog.wav", "urr.wav"];

/**
 * Plays one named line on the peon's voice channel, so the header's mouth and bubble follow it: a
 * push's own line (shared/push-sounds.ts PUSH_SOUND_LINES) or the timed note's ring line, from the
 * original set ("original", public/audio/peons-original) or the Warcraft one. `name` is the .wav
 * name with or without its extension; the .m4a plays where it is encoded. It replaces whatever line
 * is playing, is not tied to a press (so it never waits for a gesture to retry), and stays quiet
 * during the tour. Resolves the playing clip, or null when the sound could not start (no file, or
 * the browser has not let the page make sound yet).
 */
export async function playVoiceFile(set: "original" | "warcraft", name: string): Promise<PlayingClip | null> {
  if (tourActive) return null;
  const base = set === "original" ? PEON_BASE_ORIGINAL : PEON_BASE;
  const file = name.endsWith(".wav") ? name : `${name}.wav`;
  stopLaunchAudio();
  const request = launchRequest;
  lineClass = "action";
  lineLoading = true;
  const src = await resolveSrc(base, `${base}encoded.json`, file);
  if (request !== launchRequest) return null;
  const audio = await loadClip(src);
  if (request !== launchRequest) return null;
  lineLoading = false;
  if (!audio) return null;
  launchAudio = audio;
  try {
    await audio.play();
    notifyPeonSpeech(audio, base, file);
    return audio;
  } catch {
    return null;
  }
}

/** Plays a fresh peon line (Warcraft, or the original replacement when the flag is on), replacing any previous line or deferred retry. */
export async function playRandomPeonLine(): Promise<void> {
  await playLine("idle", "priority");
}

// ---------- Lines by name (shared/voice-player.ts, shared/voicelines.ts) ----------

/** Each clip's playable source this page load, by its address: a blob URL of the fetched WAV. */
const personalSrcs = new Map<string, Promise<string | null>>();

/**
 * `key` said to `name`, fetched once per page load with the device's name. Its address holds the
 * name and the line's version and the server marks it immutable, so the browser keeps it across
 * loads and only a rename (or a new version) asks the server again. Null when it cannot be had
 * (no name, offline, signed out, the server could not make it); a failure is not remembered.
 */
function personalLine(key: PersonalLineKey, name: string): Promise<ChosenLine | null> {
  const url = personalLineUrl(key, name);
  const text = personalLineText(key, name);
  if (!url || !text) return Promise.resolve(null);
  let pending = personalSrcs.get(url);
  if (!pending) {
    pending = (async () => {
      const res = await fetch(url, { credentials: "include", headers: personalHeaders(name) });
      if (!res.ok) return null;
      const bytes = new Uint8Array(await res.arrayBuffer());
      notePersonalClip(url, text, bytes);
      return URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
    })().catch(() => null);
    personalSrcs.set(url, pending);
    void pending.then((src) => {
      if (!src) personalSrcs.delete(url);
    });
  }
  return pending.then((src) => (src ? { base: PERSONAL_BASE, name: url, src } : null));
}

/** Whether this page load has greeted yet: the launch greeting is once, every later return is an ordinary line. */
let launchGreeted = false;

/**
 * The launch greeting. With lines by name on (helloUI, the original voice, a name) the peon says
 * hello by name: at once from the kept clip, or on a name's first launch the new clip if the
 * server makes it within HELLO_WAIT_MS. Otherwise an ordinary line, as every return gets. While
 * it waits the greeting counts as loading, so action lines and the rush yield to it as they would
 * to any greeting, and anything that speaks meanwhile wins.
 */
async function playLaunchGreeting(): Promise<void> {
  const name = getPeonName();
  if (!name || !launchSaysHello(isFeatureEnabled("helloUI"), isFeatureEnabled("originalAssets"), name)) return playRandomPeonLine();
  stopLaunchAudio();
  const request = launchRequest;
  lineClass = "priority";
  lineLoading = true;
  const chosen = await within(personalLine("hello", name), HELLO_WAIT_MS);
  if (request !== launchRequest) return;
  lineLoading = false;
  if (!chosen) return playRandomPeonLine();
  await playLine("idle", "priority", chosen);
}

/** The greeting for a launch or a return: hello by name the first time this page load greets, an ordinary line after. */
function greet(): void {
  if (launchGreeted) void playRandomPeonLine();
  else {
    launchGreeted = true;
    void playLaunchGreeting();
  }
}

// ---------- Transcripts (src/speech.ts) ----------
//
// The words behind a voice file, for the header's transcription: lines.json beside the original
// lines (file -> text, written by scripts/make-peon-voice.py) and the tour manifest for readings and
// ad-libs. The Warcraft set has no transcript.
const lineTexts = new Map<string, Promise<Record<string, string> | null>>();

function loadLineTexts(url: string): Promise<Record<string, string> | null> {
  let pending = lineTexts.get(url);
  if (!pending) {
    pending = fetch(url, { cache: "no-cache" })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = (await res.json()) as unknown;
        if (!data || typeof data !== "object") return null;
        const map: Record<string, string> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) if (typeof value === "string") map[key] = value;
        return map;
      })
      .catch(() => null);
    lineTexts.set(url, pending);
  }
  return pending;
}

/** What `name` in `dir` says, or null when there is no transcript for it. */
export async function speechTextFor(dir: string, name: string): Promise<string | null> {
  if (dir === PERSONAL_BASE) return personalText(name);
  if (dir === PEON_BASE_ORIGINAL) return (await loadLineTexts(`${dir}lines.json`))?.[name] ?? null;
  if (dir === TOUR_BASE) {
    const manifest = await loadTourManifest();
    if (!manifest) return null;
    for (const entry of Object.values(manifest)) {
      if (entry.file === name) return `${entry.title}${/[.!?]$/.test(entry.title) ? "" : "."} ${entry.body}`;
      if (entry.adlib === name) return entry.adlibText ?? null;
    }
  }
  return null;
}

// ---------- The tutorial (src/components/Tutorial.tsx) ----------
//
// While the tour is open the peon reads each card aloud and says nothing else: every greeting,
// action line, idle remark and rush announcement is dropped (not queued) until it closes. The
// readings live in public/audio/tour/ (scripts/make-tour-voice.py renders them from the tour's own
// copy) with a manifest of {slug: {title, body, file}}. A card is found by its title and body
// across every entry, not by its slug, so a card's alternate bodies (bodyPlain, bodyNoTask, read
// as <slug>-plain and <slug>-no-task) play too. A card whose copy no longer matches any recording
// stays silent rather than reading stale text. Only the original set has a tour voice.
const TOUR_BASE = "/audio/tour/";
/** `adlib` is the peon's own remark on the card, played this long after the reading ends. */
interface TourEntry { title: string; body: string; file: string; adlib?: string; adlibText?: string }
export const TOUR_ADLIB_GAP_MS = 500;
let tourManifest: Promise<Record<string, TourEntry> | null> | null = null;
let tourActive = false;

export function beginTourAudio(): void {
  tourActive = true;
  stopLaunchAudio();
}

export function endTourAudio(): void {
  tourActive = false;
  stopLaunchAudio();
  // The tour was activity: no idle remark right after it.
  lastPeonRequestAt = Date.now();
}

function loadTourManifest(): Promise<Record<string, TourEntry> | null> {
  if (!tourManifest) {
    tourManifest = fetch(`${TOUR_BASE}manifest.json`, { cache: "no-cache" })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = (await res.json()) as unknown;
        if (!data || typeof data !== "object") return null;
        const map: Record<string, TourEntry> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
          const entry = value as Partial<TourEntry> | null;
          if (entry && typeof entry.title === "string" && typeof entry.body === "string" && typeof entry.file === "string") {
            map[key] = { title: entry.title, body: entry.body, file: entry.file };
            if (typeof entry.adlib === "string") map[key].adlib = entry.adlib;
            if (typeof entry.adlibText === "string") map[key].adlibText = entry.adlibText;
          }
        }
        return map;
      })
      .catch(() => null);
  }
  return tourManifest;
}

/**
 * Reads one tour card, replacing whatever reading came before it, then (after a beat) adds the
 * peon's own remark on it when the card has one. Resolves true when the reading played.
 */
export async function playTourStep(title: string, body: string): Promise<boolean> {
  if (!tourActive || !isFeatureEnabled("originalAssets")) return false;
  stopLaunchAudio();
  const request = launchRequest;
  lineClass = "tour";
  lineLoading = true;
  const manifest = await loadTourManifest();
  const entry = manifest ? Object.values(manifest).find((e) => e.title === title && e.body === body) : undefined;
  if (request !== launchRequest) return false;
  if (!entry) {
    lineLoading = false;
    return false;
  }
  const played = await playTourFile(request, entry.file);
  if (played && entry.adlib) {
    const adlib = entry.adlib;
    const reading = launchAudio;
    reading?.addEventListener("ended", () => {
      setTimeout(() => {
        // Still on this card, and nothing else has taken the voice meanwhile.
        if (request === launchRequest && tourActive) void playTourFile(request, adlib);
      }, TOUR_ADLIB_GAP_MS);
    }, { once: true });
  }
  return played;
}

async function playTourFile(request: number, name: string): Promise<boolean> {
  lineLoading = true;
  const src = await resolveSrc(TOUR_BASE, `${TOUR_BASE}encoded.json`, name);
  if (request !== launchRequest) return false;
  const audio = await loadClip(src);
  // Superseded while it was fetched and decoded: dropped before it makes a sound.
  if (request !== launchRequest) return false;
  lineLoading = false;
  if (!audio) return false;
  launchAudio = audio;
  try {
    await audio.play();
    notifyPeonSpeech(audio, TOUR_BASE, name);
    return true;
  } catch {
    // The first card can open without a tap; the next tap (Next) starts the next card anyway.
    return false;
  }
}

/** The dedicated greeting for opening Add Task. */
export async function playAddTaskGreeting(): Promise<void> {
  await playLine("greeting", "priority");
}

/**
 * Plays a random line of `role` (see PEON_ROLES). greeting, done and idle are priority lines, exactly
 * like playAddTaskGreeting, playWorkComplete and playRandomPeonLine. Every other role is an action
 * line: it plays on the action, ignores the incidental cooldown, and follows two rules:
 *   - it never interrupts a greeting or completion that is loading, playing or waiting to retry: it is
 *     dropped (not queued), because a late line about an earlier tap reads as a glitch;
 *   - between action lines the later one wins: it stops the earlier one, so two never overlap.
 * It also stays quiet while the page is hidden and just after a camera hand-off (silencePeon), and
 * never retries after a blocked autoplay. Resolves true when the line went ahead (it may still be
 * silent if the set has no line for the role), false when it was dropped.
 */
export async function playPeonLine(role: PeonRole): Promise<boolean> {
  return playLine(role, PRIORITY_ROLES.has(role) ? "priority" : "action");
}

/** Plays a line of `role`, or `chosen` in its place (a line by name) with the same rules. */
async function playLine(role: PeonRole, kind: LineClass, chosen?: ChosenLine): Promise<boolean> {
  if (tourActive) return false; // the tour has the peon's voice (playTourStep)
  // Most lines follow a tap; wake the context while this is still that tap's handler.
  wakeAudio();
  // A submit plays its chiptune cue and its action line together; the line waits a beat so the
  // cue is heard rather than buried under the first word.
  if (kind === "action") {
    const wait = lastAckAt + ACK_LEAD_MS - Date.now();
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
  }
  const cfg = peonAudioConfig();
  if (kind === "action") {
    if (priorityLineBusy() || Date.now() < quietUntil || document.visibilityState === "hidden") return false;
    if (!mayHaveLines(role, cfg)) return true;
  }
  if (kind !== "incidental") lastPeonRequestAt = Date.now();
  stopLaunchAudio();
  const request = launchRequest;
  lineClass = kind;
  lineLoading = true;
  let base = cfg.base;
  let name: string;
  let src: string;
  if (chosen) ({ base, name, src } = chosen);
  else {
    const list = await linesFor(role, cfg);
    if (request !== launchRequest) return noteLine(role, "dropped", "superseded");
    if (list.length === 0) {
      lineLoading = false;
      return noteLine(role, "dropped", "no lines");
    }
    name = pick(list);
    src = await resolveSrc(cfg.base, cfg.encodedUrl, name);
    if (request !== launchRequest) return noteLine(role, "dropped", "superseded");
  }
  const audio = await loadClip(src);
  // Superseded while it was fetched and decoded: dropped before it makes a sound.
  if (request !== launchRequest) return noteLine(role, "dropped", "superseded");
  lineLoading = false;
  if (!audio) return noteLine(role, "dropped", "not loaded");

  launchAudio = audio;
  try {
    await audio.play();
    notifyPeonSpeech(audio, base, name);
    noteLine(role, "played", name);
    return true;
  } catch (e) {
    if (request !== launchRequest) return noteLine(role, "dropped", "superseded");
    // Only greetings and completions wait for a gesture; a remark or action line belongs to its moment.
    if (kind !== "priority") return true;
    // A browser may require a tap or key press before allowing sound.
    emitAudioEvent("audio.line.blocked", role, { state: contextState(), error: e instanceof Error ? e.name : String(e) });
  }

  const cleanup = () => {
    window.removeEventListener("pointerup", onGesture, { capture: true });
    window.removeEventListener("keydown", onGesture, { capture: true });
    removePendingGesture = () => {};
    retryPending = false;
  };
  const onGesture = () => {
    cleanup();
    if (request === launchRequest && document.visibilityState !== "hidden") {
      audio
        .play()
        .then(() => notifyPeonSpeech(audio, base, name))
        .catch(() => {});
    }
  };
  removePendingGesture = cleanup;
  retryPending = true;
  window.addEventListener("pointerup", onGesture, { capture: true });
  window.addEventListener("keydown", onGesture, { capture: true });
  return true;
}

// ---------- Gold rush announcement (src/views/Board.tsx) ----------

const RUSH_HEARD_KEY = "op.rushHeard";
/** The last rush announced in this page, for when localStorage is unavailable. */
let rushHeardHere: string | null = null;
/** A rush counts as just started for its first half hour: long enough to cover opening the app from its push. */
export const RUSH_FRESH_MS = 30 * 60_000;
/** How long the rush line waits for a greeting or completion to finish before giving up. */
const RUSH_WAIT_MS = 10_000;

/** Whether `flash` is a rush this device has not announced, still unclaimed and in its first RUSH_FRESH_MS. */
export function rushIsNews(flash: FlashState | null, heardId: string | null, now: number): boolean {
  const active = flash?.active;
  if (!active || flash.claimed || active.id === heardId) return false;
  const started = new Date(active.starts_at).getTime();
  return Number.isFinite(started) && now >= started && now - started < RUSH_FRESH_MS;
}

/**
 * Called whenever the board's flash state changes: says the rush line once per rush on this device.
 * The board usually learns of a rush as the app opens, while the launch greeting is still playing,
 * so this one line waits (up to RUSH_WAIT_MS) for the peon to finish instead of being dropped.
 */
export function announceGoldRush(flash: FlashState | null, now = Date.now()): void {
  let heard = rushHeardHere;
  try {
    heard = localStorage.getItem(RUSH_HEARD_KEY) ?? rushHeardHere;
  } catch {
    /* storage unavailable: at worst the rush is announced once per page load */
  }
  const active = flash?.active;
  if (!active || !rushIsNews(flash, heard, now)) return;
  rushHeardHere = active.id;
  try {
    localStorage.setItem(RUSH_HEARD_KEY, active.id);
  } catch {
    /* ignore */
  }
  const deadline = Date.now() + RUSH_WAIT_MS;
  const attempt = () => {
    if (priorityLineBusy() && Date.now() < deadline) window.setTimeout(attempt, 500);
    else void playPeonLine("rush");
  };
  attempt();
}

/** A return within this long of a camera or library hand-off is the hand-off ending, not the app coming to the front. */
export const HANDOFF_RETURN_MS = 10 * 60_000;

/**
 * The greeting: once at launch (by name, with lines by name on: playLaunchGreeting) and once each
 * time the app comes back to the front, an ordinary line, which is a
 * visibilitychange from hidden to visible and nothing else. Window focus is not a return: a blur
 * and focus pair happens on a file picker, a permission prompt or a tap into another frame, and
 * the greeting then played over the photo check's own cue. A return from a camera or library
 * hand-off (silencePeon) is not greeted either.
 */
export function startPeonFocusAudio(): () => void {
  let hidden = document.visibilityState === "hidden";
  const leave = () => {
    hidden = true;
    stopLaunchAudio();
  };
  const visibilityChanged = () => {
    if (document.visibilityState === "hidden") {
      leave();
      return;
    }
    if (!hidden) return;
    hidden = false;
    const now = Date.now();
    const handingOff = now < quietUntil || (handoffAt !== 0 && now - handoffAt < HANDOFF_RETURN_MS);
    handoffAt = 0;
    if (handingOff) return;
    greet();
  };
  window.addEventListener("pagehide", leave);
  document.addEventListener("visibilitychange", visibilityChanged);
  if (!hidden) greet();
  return () => {
    window.removeEventListener("pagehide", leave);
    document.removeEventListener("visibilitychange", visibilityChanged);
    stopLaunchAudio();
  };
}

async function playFile(file: string): Promise<void> {
  const src = await resolveSrc(BASE, UI_ENCODED_URL, file);
  const clip = await loadClip(src);
  clip?.play().catch(() => {});
}

// ---------- The timed note's alarm (src/ring.ts, docs/timed-notes.md) ----------
//
// The chiptune chime from public/audio/ui/alarm.wav (.m4a where encoded, the same lookup as every
// cue), looped on one buffer source until stopped or `maxMs` pass. Only one loop ever plays: a new
// start stops the old one. It is never started from a press, so it does not wake the context: when
// the page has not been allowed sound yet, the start resolves false and the ringing card shows in
// silence rather than throwing.

let alarmLoop: { stop: () => void } | null = null;
let alarmRequest = 0;

/** Starts the looping alarm; resolves true once it is sounding. */
export async function startAlarmLoop(maxMs: number): Promise<boolean> {
  stopAlarmLoop();
  const request = alarmRequest;
  const src = await resolveSrc(BASE, UI_ENCODED_URL, "alarm.wav");
  if (request !== alarmRequest) return false;
  const ctx = audioCtx();
  let stop: () => void;
  if (ctx) {
    let buffer: AudioBuffer;
    try {
      const res = await fetch(src);
      if (!res.ok) return false;
      buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      return false;
    }
    if (request !== alarmRequest || !(await contextRunning(ctx)) || request !== alarmRequest) return false;
    try {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(ctx.destination);
      source.start();
      stop = () => {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
      };
    } catch {
      return false;
    }
  } else {
    const audio = new Audio(src);
    audio.loop = true;
    try {
      await audio.play();
    } catch {
      return false;
    }
    if (request !== alarmRequest) {
      audio.pause();
      return false;
    }
    stop = () => audio.pause();
  }
  const timer = setTimeout(stopAlarmLoop, maxMs);
  alarmLoop = {
    stop: () => {
      clearTimeout(timer);
      stop();
    },
  };
  return true;
}

/** Ends the alarm loop, and any start still loading. */
export function stopAlarmLoop(): void {
  alarmRequest++;
  alarmLoop?.stop();
  alarmLoop = null;
}

/** Whether the alarm is sounding now. */
export function alarmLooping(): boolean {
  return alarmLoop !== null;
}

// ---------- The trinkets' tunes (docs/trinkets.md) ----------

// One tune at a time, looped through the shared context while a trinket is looked at (the find
// sheet, the collection's viewer), outside the peon's voice channel: he keeps quiet while it plays
// only because nothing asks him to speak. A start while one plays stops the old one; a start the
// context refuses (no gesture yet) resolves false and the trinket turns in silence.

let tuneLoop: { stop: () => void } | null = null;
let tuneRequest = 0;

/** Starts a trinket's tune, looping; resolves true once it is sounding. */
export async function startTrinketTune(key: TrinketKey): Promise<boolean> {
  stopTrinketTune();
  const request = tuneRequest;
  const src = await resolveSrc(TUNE_BASE, `${TUNE_BASE}encoded.json`, tuneFile(key));
  if (request !== tuneRequest) return false;
  const ctx = audioCtx();
  let stop: () => void;
  if (ctx) {
    let buffer: AudioBuffer;
    try {
      const res = await fetch(src);
      if (!res.ok) return false;
      buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      return false;
    }
    if (request !== tuneRequest || !(await contextRunning(ctx)) || request !== tuneRequest) return false;
    try {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = TUNE_GAIN;
      source.connect(gain);
      gain.connect(ctx.destination);
      source.start();
      stop = () => {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
      };
    } catch {
      return false;
    }
  } else {
    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = TUNE_GAIN;
    try {
      await audio.play();
    } catch {
      return false;
    }
    if (request !== tuneRequest) {
      audio.pause();
      return false;
    }
    stop = () => audio.pause();
  }
  tuneLoop = { stop };
  return true;
}

/** Ends the tune, and any start still loading. */
export function stopTrinketTune(): void {
  tuneRequest++;
  tuneLoop?.stop();
  tuneLoop = null;
}

/** Whether a tune is sounding now. */
export function trinketTunePlaying(): boolean {
  return tuneLoop !== null;
}

/** The tunes sit under the voice lines: a music box in the next room, not a jingle in the ear. */
const TUNE_GAIN = 0.6;

/** How long an action line holds back after a chiptune cue (the cues are about 170 ms). */
export const ACK_LEAD_MS = 220;
let lastAckAt = 0;

/** Plays one of the acknowledgement cues (or any cue if none are installed). Call from a user gesture. */
export async function playAcknowledgement(): Promise<void> {
  lastAckAt = Date.now();
  // A submit gets no tick (startUiClickAudio leaves it to this cue), so this is its wake-up. A
  // button outside a form (the photo check's Add task, Sign in with a passkey) did tick: the
  // acknowledgement replaces that tick, as a screen cue does, so one press makes one sound.
  if (lastCue?.name === "click") lastCue.stop();
  wakeAudio();
  const list = await loadManifest();
  if (list.length === 0) return;
  const acks = ACK_LINES.filter((f) => list.includes(f));
  await playFile(pick(acks.length ? acks : list));
}

// ---------- UI cues: a tick on every press, a note on every screen (owner's ruling, 2026-09-24) ----------
//
// Small chiptune cues from public/audio/ui (scripts/make-ui-audio.mjs): "click" for any button,
// "open" when a screen, sheet or viewer appears and "close" when one goes or the board comes back.
// They never touch the peon's voice channel. Cues that land within UI_CUE_MERGE_MS of each other
// (a button that opens a sheet, a sheet that closes into a screen) collapse to the last one.
export const UI_CUE_MERGE_MS = 120;
export type UiCue = "click" | "open" | "close";
const UI_CUES: readonly UiCue[] = ["click", "open", "close"];
let lastCue: { name: UiCue; stop: () => void; at: number } | null = null;

// The cues are decoded once into Web Audio buffers and played from there: fetching and decoding per
// press puts a lag behind the tap, which the owner heard. A buffer source starts on the same frame.
// These three are the only decoded sounds kept; they are a few KB each.
const cueBuffers = new Map<UiCue, AudioBuffer>();

/** Fetches and decodes the cues up front (the .wav files are a few KB each). */
export function preloadUiCues(): void {
  const ctx = audioCtx();
  if (!ctx) return;
  for (const name of UI_CUES) {
    if (cueBuffers.has(name)) continue;
    fetch(`${BASE}${name}.wav`)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(String(res.status)))))
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buffer) => cueBuffers.set(name, buffer))
      .catch(() => {});
  }
}

export async function playUiCue(name: UiCue): Promise<void> {
  const at = Date.now();
  if (lastCue && at - lastCue.at < UI_CUE_MERGE_MS) {
    if (lastCue.name === name) return; // one press, one tick
    lastCue.stop();
  }
  const cue = { name, stop: () => {}, at };
  lastCue = cue;
  const ctx = audioCtx();
  const buffer = cueBuffers.get(name);
  if (ctx && buffer) {
    try {
      // A press is a gesture, so resuming is allowed; see wakeAudio for why anything but
      // "running" gets one.
      wakeAudio();
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start();
      cue.stop = () => {
        try {
          source.stop();
        } catch {
          /* already ended */
        }
      };
      return;
    } catch {
      /* fall through to a one-off clip */
    }
  }
  // The cue is not decoded yet (a press before preloading finished) or there is no Web Audio: load
  // it like any other sound, a Clip on the shared context or, without one, an element.
  const list = await loadManifest();
  const file = `${name}.wav`;
  if (!list.includes(file) || lastCue !== cue) return;
  const src = await resolveSrc(BASE, UI_ENCODED_URL, file);
  if (lastCue !== cue) return;
  const audio = await loadClip(src);
  if (!audio || lastCue !== cue) return;
  cue.stop = () => audio.pause();
  audio.play().catch(() => {});
}

/** What ticks when pressed. A label ticks only for the checkbox or radio it holds (see isTickControl). */
const TICK_CONTROLS = "button, a[href], [role='button'], [role='tab'], summary, label, input[type='checkbox'], input[type='radio'], select";
/** Submits keep their chiptune acknowledgement instead (owner's ruling, 2026-09-24). */
const SUBMITS = "form button:not([type]), form button[type='submit'], input[type='submit']";

type Pressable = Element & { matches: (s: string) => boolean; control?: { matches?: (s: string) => boolean } | null };

/** A label ticks when it toggles a checkbox or radio; a label for a text field is not a press. */
function isTickControl(control: Pressable): boolean {
  if (!control.matches("label")) return true;
  const input = control.control;
  return !!input?.matches?.("input[type='checkbox'], input[type='radio']");
}

/**
 * A tick for every trusted press on a control, on the click (the release), so a touch that turns
 * into a scroll never ticks and a held press ticks once, with the work it starts. A press that
 * opens a screen, sheet or viewer, or follows a tab or in-app link, ticks too: its cue lands within
 * UI_CUE_MERGE_MS and replaces the tick, and pressing the tab already shown ticks alone. A label's
 * click and the click it hands its checkbox are one press, and the merge keeps them to one tick.
 * Submits are left to their acknowledgement cue. Every pointerdown still wakes the shared context,
 * so whatever the press then plays (a peon line, an acknowledgement) finds it running.
 */
export function startUiClickAudio(): () => void {
  preloadUiCues();
  const onPress = (event: Event) => {
    if (!event.isTrusted || ((event as PointerEvent).button ?? 0) !== 0) return;
    wakeAudio();
  };
  const onClick = (event: Event) => {
    if (!event.isTrusted || ((event as MouseEvent).button ?? 0) !== 0) return;
    const target = event.target as { closest?: (selector: string) => Pressable | null } | null;
    const control = target?.closest?.(TICK_CONTROLS);
    if (!control || control.matches(":disabled, [aria-disabled='true']")) return;
    if (control.matches(SUBMITS) || !isTickControl(control)) return;
    void playUiCue("click");
  };
  document.addEventListener("pointerdown", onPress, true);
  document.addEventListener("click", onClick, true);
  return () => {
    document.removeEventListener("pointerdown", onPress, true);
    document.removeEventListener("click", onClick, true);
  };
}

/** The tabs. Landing on one from a screen that is not a tab is a close. */
const TOP_ROUTES = new Set<Route["name"]>(["open", "done", "schedules", "upgrades"]);
/** The screens each screen closes back into (src/back-swipe.ts parentRoute, with a task's either board). */
const PARENTS: Partial<Record<Route["name"], readonly Route["name"][]>> = {
  task: ["open", "done"],
  newTask: ["open"],
  hall: ["open", "done"],
  rewards: ["open"],
  usage: ["open"],
  house: ["open"],
  peon: ["open"],
  newSchedule: ["schedules"],
  schedule: ["schedules"],
  newUpgrade: ["upgrades"],
  upgrade: ["upgrades"],
};

/** The route's full name ("upgrades" and "newUpgrade" differ), from the router's own parse. */
export function routeName(hash: string): Route["name"] {
  return parseHash(hash).name;
}

/**
 * The cue for a move between screens, or null when the screen did not change: "close" for a pop
 * (back through history), for a screen to the one it sits on, and for any screen landing on a tab;
 * "open" for everything else, a tab to a tab included.
 */
export function screenCue(previousHash: string, nextHash: string, pop = false): UiCue | null {
  const previous = routeName(previousHash);
  const next = routeName(nextHash);
  if (next === previous) return null;
  if (pop || PARENTS[previous]?.includes(next)) return "close";
  if (TOP_ROUTES.has(next) && !TOP_ROUTES.has(previous)) return "close";
  return "open";
}

/**
 * Each history entry the app visits is stamped with an increasing number in its state, so a move
 * to an entry with a lower number is a pop (back, Android's button, the browser's own back).
 */
const SEQ_KEY = "opSeq";
let seqCounter = 0;

function entrySeq(): number | null {
  if (typeof history === "undefined") return null;
  const state = history.state as Record<string, unknown> | null;
  const seq = state && typeof state === "object" ? state[SEQ_KEY] : undefined;
  return typeof seq === "number" ? seq : null;
}

function stampEntry(): number | null {
  if (typeof history === "undefined") return null;
  const existing = entrySeq();
  if (existing !== null) return existing;
  const seq = ++seqCounter;
  try {
    const state = history.state && typeof history.state === "object" ? history.state : {};
    history.replaceState({ ...state, [SEQ_KEY]: seq }, "");
  } catch {
    return null;
  }
  return seq;
}

export function startScreenAudio(): () => void {
  let previous = typeof location === "undefined" ? "" : location.hash;
  // A reload keeps the entries' stamps: count on from the highest one seen.
  let current = entrySeq();
  if (current !== null) seqCounter = Math.max(seqCounter, current);
  else current = stampEntry();
  const onHash = () => {
    const next = location.hash;
    const known = entrySeq();
    const pop = known !== null && current !== null && known < current;
    current = known ?? stampEntry();
    const cue = screenCue(previous, next, pop);
    previous = next;
    if (cue) void playUiCue(cue);
  };
  window.addEventListener("hashchange", onHash);
  return () => window.removeEventListener("hashchange", onHash);
}

/**
 * The Device log hears how the completion line went ("audio.line.played" or "audio.line.dropped"
 * with the context's state), because that is the line the owner missed on the phone; other roles
 * only report when blocked (playLine). Returns true so the callers' "went ahead" reads the same.
 */
function noteLine(role: PeonRole, outcome: "played" | "dropped", message: string): true {
  if (role === "done") emitAudioEvent(`audio.line.${outcome}`, message, { role, state: contextState() });
  return true;
}

/** Always plays a completion line (the done role) when a task is finished. */
export async function playWorkComplete(): Promise<void> {
  await playLine("done", "priority");
}

/**
 * Whether the peon has a greeting, completion or action line loading, playing or waiting for a
 * gesture: the update watcher (src/update.ts) holds its reload until the line is done, so a
 * completion is heard before the app refreshes itself.
 */
export function peonSpeaking(): boolean {
  if (lineLoading || retryPending) return true;
  return !!launchAudio && !launchAudio.paused && !launchAudio.ended;
}

/** Whether any peon voice lines are installed (in whichever directory the flag currently selects). */
export async function hasVoiceLines(): Promise<boolean> {
  return (await loadManifest(peonAudioConfig().manifestUrl)).length > 0;
}
